import { useState } from "react";
import { ArrowRight, Search } from "lucide-react";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { BUDGET_OPTIONS, PROPERTY_TYPES } from "@/config/siteConfig";
import { scrollToId } from "@/lib/scroll";
import { Reveal } from "@/components/Reveal";

const labelCls = "block text-[11px] font-semibold uppercase tracking-[0.14em] text-ink-soft mb-2";
const triggerCls =
  "h-12 w-full rounded-lg border-line bg-warm/60 text-[14px] font-medium text-ink focus:ring-1 focus:ring-gold focus:border-gold";

export const SearchBox = ({ onSearch, onEnquire }) => {
  const [lookingFor, setLookingFor] = useState("Buy");
  const [type, setType] = useState("");
  const [location, setLocation] = useState("");
  const [budget, setBudget] = useState("");

  const submit = (e) => {
    e.preventDefault();
    if (lookingFor === "Sell") {
      onEnquire({ interest: "Sell", message: "I want to sell my property. Please guide me with the next steps." });
      return;
    }
    onSearch({ lookingFor, type, location: location.trim(), budget });
    scrollToId("#properties");
  };

  return (
    <div className="relative z-30 max-w-[1280px] mx-auto px-4 sm:px-6 lg:px-8">
      <Reveal y={40}>
        <form
          onSubmit={submit}
          data-testid="property-search-box"
          className="-mt-20 sm:-mt-24 bg-white rounded-xl border border-line shadow-[0_24px_60px_rgba(11,31,51,0.14)] p-6 sm:p-8"
        >
          <div className="flex items-center gap-3 mb-6">
            <span className="h-8 w-8 rounded-lg bg-navy flex items-center justify-center">
              <Search size={15} className="text-gold" />
            </span>
            <h2 className="font-display text-lg sm:text-xl font-semibold text-navy">What are you looking for?</h2>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-[1fr_1fr_1fr_1fr_auto] gap-4 lg:gap-5 items-end">
            <div>
              <label className={labelCls}>I Want To</label>
              <Select value={lookingFor} onValueChange={setLookingFor}>
                <SelectTrigger className={triggerCls} data-testid="property-search-looking-for">
                  <SelectValue placeholder="Buy" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="Buy">Buy</SelectItem>
                  <SelectItem value="Sell">Sell</SelectItem>
                  <SelectItem value="Rent">Rent</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className={labelCls}>Property Type</label>
              <Select value={type} onValueChange={setType}>
                <SelectTrigger className={triggerCls} data-testid="property-search-type">
                  <SelectValue placeholder="Select Property Type" />
                </SelectTrigger>
                <SelectContent>
                  {PROPERTY_TYPES.map((t) => (
                    <SelectItem key={t} value={t}>{t}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className={labelCls}>Location</label>
              <input
                value={location}
                onChange={(e) => setLocation(e.target.value)}
                placeholder="Enter Location"
                data-testid="property-search-location"
                className="h-12 w-full rounded-lg border border-line bg-warm/60 px-3.5 text-[14px] font-medium text-ink placeholder:text-ink-soft/60 focus:outline-none focus:ring-1 focus:ring-gold focus:border-gold transition"
              />
            </div>
            <div>
              <label className={labelCls}>Budget</label>
              <Select value={budget} onValueChange={setBudget}>
                <SelectTrigger className={triggerCls} data-testid="property-search-budget">
                  <SelectValue placeholder="Select Budget" />
                </SelectTrigger>
                <SelectContent>
                  {BUDGET_OPTIONS.map((b) => (
                    <SelectItem key={b.value} value={b.value}>{b.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <button
              type="submit"
              data-testid="property-search-submit-button"
              className="h-12 bg-gold text-navy hover:bg-gold-dark hover:-translate-y-0.5 transition-all duration-300 rounded-lg px-6 text-[15px] font-semibold flex items-center justify-center gap-2 shadow-md shadow-gold/25 w-full lg:w-auto"
            >
              Search Property <ArrowRight size={16} />
            </button>
          </div>
        </form>
      </Reveal>
    </div>
  );
};
